import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import messages from "./local/index";
import { hasConsent, onConsentChange } from "@/lib/cookieConsent";

export const LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "bg", label: "BG", name: "Български" },
] as const;

export type Language = (typeof LANGUAGES)[number]["code"];

const LANG_KEY = "aistyle:lang";

/** The Intl locale used for prices and dates in each language. */
export const LOCALES: Record<Language, string> = { en: "en-IE", bg: "bg-BG" };

/** The active language, narrowed to one we ship. */
export function currentLanguage(): Language {
  return i18n.resolvedLanguage === "bg" ? "bg" : "en";
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Bulgarian is the default. English stays second, so a string missing in
    // Bulgarian shows in English rather than as a raw key.
    fallbackLng: ["bg", "en"],
    supportedLngs: LANGUAGES.map((l) => l.code),
    // "bg-BG" from the browser resolves to "bg".
    nonExplicitSupportedLngs: true,
    debug: false,
    resources: messages,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // ?lang=en in a link wins, then the visitor's own choice from the switcher.
      // The browser's language is not consulted: everyone else gets Bulgarian.
      order: ["querystring", "localStorage"],
      lookupQuerystring: "lang",
      lookupLocalStorage: LANG_KEY,
      // Saved by rememberLanguage below, and only with consent.
      caches: [],
    },
  });

// Screen readers and the browser's own translate prompt read <html lang>.
const syncHtmlLang = () => {
  document.documentElement.lang = currentLanguage();
};
syncHtmlLang();
i18n.on("languageChanged", syncHtmlLang);

// Remembering the language is a "preferences" cookie: only with consent, and
// as soon as it is given, so a choice made before the banner is not lost.
const rememberLanguage = () => {
  if (!hasConsent("preferences")) return;
  try {
    localStorage.setItem(LANG_KEY, currentLanguage());
  } catch {
    /* storage blocked */
  }
};
i18n.on("languageChanged", rememberLanguage);
onConsentChange(rememberLanguage);

export default i18n;
